<template>
  <div class="map-page">
    <gmap-map
      ref="googleMapRef"
      :center="getDefaultMapCenter()"
      :zoom="getDefaultZoomLevel()"
      map-type-id="roadmap"
      :options="{
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        mapTypeControlOptions: { position: 3 },
      }"
      class="map-canvas"
    >
      <!-- 圃場名ラベル -->
      <template v-if="layers.fields">
        <gmap-marker v-for="(m, i) in markers" :key="'f-' + i" :position="m.position" :label="m.label" :icon="m.icon" />
      </template>

      <!-- 地点メモ (POI) ピン -->
      <template v-if="layers.poi">
        <gmap-marker
          v-for="r in pois"
          :key="'poi-' + r.id"
          :position="{ lat: r.la, lng: r.lo }"
          :icon="poiIcon(r)"
          @click="poiOpen = r.id"
        />
      </template>

      <!-- 基準線 -->
      <template v-if="layers.reflines">
        <gmap-polyline
          v-for="rl in refLinePaths"
          :key="'rl-' + rl.id"
          :path="rl.path"
          :options="{ strokeColor: '#2196F3', strokeWeight: 2, strokeOpacity: 0.9 }"
        />
      </template>

      <!-- 走行・移動軌跡 (期間内の GNSS 記録) -->
      <template v-if="layers.tasks">
        <gmap-polyline
          v-for="t in taskRoutes"
          :key="'tr-' + t.id"
          :path="t.path"
          :options="{ strokeColor: '#43A047', strokeWeight: 4, strokeOpacity: 0.9, zIndex: 1 }"
          @click="taskOpen = t.id"
        />
      </template>

      <!-- 作業履歴 (F5-3 work_records: Trajectory 参照+演算条件を評価して描画) -->
      <template v-if="layers.works">
        <gmap-polyline
          v-for="(seg, i) in workSegments"
          :key="'wk-' + seg.id + '-' + i"
          :path="seg.path"
          :options="{ strokeColor: '#8E24AA', strokeWeight: 4, strokeOpacity: 0.9, zIndex: 2 }"
          @click="workOpen = seg.id"
        />
      </template>
    </gmap-map>

    <!-- 走行軌跡の詳細 (経路クリックで表示) -->
    <v-card v-if="selectedTaskRoute" class="map-poi-card" elevation="6">
      <v-card-title class="py-2 subtitle-2">
        {{ selectedTaskRoute.op }}
        <v-spacer />
        <v-btn icon x-small @click="taskOpen = null"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="py-1">
        <div class="caption grey--text">{{ selectedTaskRoute.start }}</div>
        <v-btn small text color="primary" class="pl-0" @click="$router.push('/tasks/' + selectedTaskRoute.id)">
          {{ $tt('customDetail') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- 作業履歴 (派生) の詳細: マップはビューアー専任なので編集・削除は /works で行う -->
    <v-card v-if="selectedWork" class="map-poi-card" elevation="6">
      <v-card-title class="py-2 subtitle-2">
        <v-chip x-small color="purple" text-color="white" class="mr-2">{{ $tt('feed.derivedChip') }}</v-chip>
        {{ selectedWork.name || selectedWork.kind }}
        <v-spacer />
        <v-btn icon x-small @click="workOpen = null"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="py-1">
        <div v-if="selectedWork.memo">{{ selectedWork.memo }}</div>
        <div v-if="selectedWork.cache && selectedWork.cache.distance_m" class="caption grey--text">
          {{ (selectedWork.cache.distance_m / 1000).toFixed(2) }} km
        </div>
        <v-btn small text color="primary" class="pl-0" @click="$router.push('/works/' + selectedWork.id)">
          {{ $tt('customDetail') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- POI 詳細 (ピンクリックで表示) -->
    <v-card v-if="selectedPoi" class="map-poi-card" elevation="6">
      <v-card-title class="py-2 subtitle-2">
        {{ selectedPoi.title }}
        <v-spacer />
        <v-btn icon x-small @click="poiOpen = null"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="py-1">
        <div v-if="selectedPoi.memo">{{ selectedPoi.memo }}</div>
        <img
          v-for="p in selectedPoi.photos"
          :key="p.id"
          :src="p.thumb_url"
          style="width: 100%; border-radius: 4px; margin-top: 6px"
        />
      </v-card-text>
    </v-card>

    <!-- レイヤーツリー (Google Earth 風ビューアー) -->
    <v-card class="map-layers" elevation="4">
      <v-card-title class="py-2 subtitle-2">
        <v-icon small left>mdi-layers</v-icon>
        {{ $tt('feed.mapLayers') }}
      </v-card-title>
      <v-card-text class="py-0">
        <v-checkbox v-model="layers.fields" dense hide-details class="mt-0" :label="$tt('customFieldHeader')" />
        <v-checkbox v-model="layers.poi" dense hide-details class="mt-0" :label="$tt('feed.poiRecords')" />
        <v-checkbox v-model="layers.reflines" dense hide-details class="mt-0" :label="$tt('customRefLine')" />
        <v-checkbox v-model="layers.tasks" dense hide-details class="mt-0" :label="$tt('feed.trajectories')" />
        <template v-if="layers.tasks">
          <v-text-field
            v-model="taskFrom"
            type="date"
            dense
            hide-details
            class="mt-1"
            :label="$tt('customDateFilterStartDate')"
          />
          <v-text-field
            v-model="taskTo"
            type="date"
            dense
            hide-details
            class="mt-1 mb-2"
            :label="$tt('customDateFilterEndDate')"
          />
          <div class="caption grey--text mb-2">{{ taskRoutes.length }} / {{ taskCount }}</div>
        </template>
        <v-checkbox
          v-model="layers.works"
          dense
          hide-details
          class="mt-0 mb-2"
          :label="$tt('DocFormTabNameTaskRecordList')"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import OperationTypeMixin from '@/components/mixins/operation-type-mixin'
import * as Constants from '@/lib/constants'
import connectDatastoreClient from '@/lib/connect-client/connect-datastore-client'
import connectNaviClient from '@/lib/connect-client/connect-navi-client'
import feedApiClient from '@/lib/feed-api-client'
import UserInfo from '@/lib/user-info'
import * as turf from '@turf/turf'

export default {
  name: 'MapView',
  mixins: [GoogleMapMixin, OperationTypeMixin],
  data() {
    return {
      layers: { fields: true, poi: true, tasks: true, reflines: false, works: true },
      records: [],
      poiOpen: null,
      taskFrom: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
      taskTo: new Date().toISOString().slice(0, 10),
      taskRoutes: [],
      taskCount: 0,
      taskOpen: null,
      taskLoadSeq: 0,
      refLinePaths: [],
      refLinesLoaded: false,
      works: [],
      workSegments: [],
      worksLoaded: false,
      workOpen: null,
    }
  },
  computed: {
    pois() {
      return this.records.filter(r => r.kind === 'poi' && r.la != null && r.lo != null)
    },
    storeFields() {
      return this.$store.getters.fields
    },
    selectedPoi() {
      return this.pois.find(r => r.id === this.poiOpen) || null
    },
    selectedTaskRoute() {
      return this.taskRoutes.find(t => t.id === this.taskOpen) || null
    },
    selectedWork() {
      return this.works.find(w => w.id === this.workOpen) || null
    },
  },
  watch: {
    'layers.fields'(on) {
      this.applyFieldVisibility(on)
    },
    // 圃場はトークン復元後に非同期ロードされるため、届いたら再描画してズームし直す
    storeFields() {
      this.initMap()
    },
    taskFrom() {
      this.loadTaskRoutes()
    },
    taskTo() {
      this.loadTaskRoutes()
    },
    'layers.tasks'(on) {
      if (on && this.taskRoutes.length === 0) {
        this.loadTaskRoutes()
      }
    },
    'layers.reflines'(on) {
      if (on && !this.refLinesLoaded) {
        this.loadRefLines()
      }
    },
    'layers.works'(on) {
      if (on && !this.worksLoaded) {
        this.loadWorks()
      }
    },
  },
  mounted() {
    this.initMap()
    this.loadPois()
    this.loadTaskRoutes()
    this.loadWorks()
  },
  methods: {
    initMap() {
      this.drawAllFieldsOnGoogleMap()
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.setTilt(0)
        // 再描画時は既存フィーチャを消してから追加
        map.data.forEach(f => map.data.remove(f))
        map.data.addGeoJson(this.$store.getters.fieldGeoJsons)
        map.data.setStyle({
          clickable: false,
          fillColor: Constants.FIELD_FILL_COLOR,
          fillOpacity: 0.35,
          strokeColor: Constants.FIELD_FILL_COLOR,
          strokeWeight: 1,
          zIndex: 0,
        })
        const bounds = new google.maps.LatLngBounds()
        let hasFeature = false
        map.data.forEach(feature => {
          hasFeature = true
          feature.getGeometry().forEachLatLng(latlng => bounds.extend(latlng))
        })
        for (const r of this.pois) {
          hasFeature = true
          bounds.extend({ lat: r.la, lng: r.lo })
        }
        if (hasFeature) {
          map.fitBounds(bounds)
        }
      })
    },
    applyFieldVisibility(on) {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.data.setStyle({
          clickable: false,
          visible: on,
          fillColor: Constants.FIELD_FILL_COLOR,
          fillOpacity: 0.35,
          strokeColor: Constants.FIELD_FILL_COLOR,
          strokeWeight: 1,
        })
      })
    },
    async loadTaskRoutes() {
      const seq = ++this.taskLoadSeq
      this.taskRoutes = []
      this.taskCount = 0
      const username = this.$store.getters.loginUsername
      if (!username || !this.taskFrom || !this.taskTo) return
      const filter = {
        dateTimeRange: {
          type: 'Start',
          from: new Date(this.taskFrom + 'T00:00:00').toISOString(),
          to: new Date(this.taskTo + 'T23:59:59').toISOString(),
        },
        queries: [{ type: 'Account', uuids: [username] }],
      }
      try {
        const client = UserInfo.isFSMigrated() ? connectDatastoreClient : connectNaviClient
        const res = await client.getTasksWithFilter(filter, { page: 0, pageSize: 30 })
        const tasks = (res.data && res.data.contents) || []
        this.taskCount = tasks.length
        for (const task of tasks) {
          if (seq !== this.taskLoadSeq) return // 期間が変わったら中断
          const sep = task.id.indexOf('-')
          if (sep < 0) continue
          try {
            const r = await feedApiClient.getRoute(task.id.slice(0, sep), task.id.slice(sep + 1))
            const pts = (r.data.route || []).map(p => ({ lat: p[0], lng: p[1] }))
            if (pts.length > 1 && seq === this.taskLoadSeq) {
              this.taskRoutes.push({
                id: task.id,
                op: this.getLocalizedOperationType(task.operationType),
                start: task.startTime
                  ? new Date(task.startTime).toLocaleString(undefined, {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '',
                path: pts,
              })
            }
          } catch (e) {
            // 経路なしはスキップ
          }
        }
      } catch (e) {
        console.error(e.response || e)
      }
      // 作業履歴一覧「マップで表示」からの遷移 (?task=) は該当経路にズーム
      const focusTask = this.$route.query.task
      if (focusTask && seq === this.taskLoadSeq) {
        const t = this.taskRoutes.find(r => r.id === focusTask)
        if (t) {
          this.fitToPaths([t.path])
          this.taskOpen = t.id
        }
      }
    },
    fitToPaths(paths) {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        const bounds = new google.maps.LatLngBounds()
        let has = false
        for (const path of paths) {
          for (const p of path) {
            has = true
            bounds.extend(p)
          }
        }
        if (has) {
          map.fitBounds(bounds)
        }
      })
    },
    poiIcon(r) {
      // 写真つき地点メモはサムネイルをピンにする (R14)
      if (r.photos && r.photos.length) {
        return {
          url: r.photos[0].thumb_url,
          scaledSize: { width: 48, height: 36 },
          anchor: { x: 24, y: 18 },
        }
      }
      return undefined
    },
    async loadRefLines() {
      this.refLinesLoaded = true
      try {
        const client = UserInfo.isFSMigrated() ? connectDatastoreClient : connectNaviClient
        const res = await client.listRefLines({ page: 0, pageSize: 30 })
        const lines = (res.data && (res.data.contents || res.data.items)) || []
        for (const rl of lines.slice(0, 30)) {
          try {
            const g = await client.getRefLineGeoJson(rl)
            const feature = g.data && g.data.features && g.data.features[0]
            const coords = feature && feature.geometry && feature.geometry.coordinates
            if (coords && coords.length > 1) {
              this.refLinePaths.push({
                id: rl.id,
                path: coords.map(c => ({ lat: c[1], lng: c[0] })),
              })
            }
          } catch (e) {
            // 座標なしはスキップ
          }
        }
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async loadPois() {
      try {
        const res = await feedApiClient.manualRecords()
        this.records = res.data.records || []
        if (this.pois.length && this.$store.getters.fields.length === 0) {
          this.initMap()
        }
      } catch (e) {
        console.error(e.response || e)
      }
    },
    clipPaths(paths, polygonCoords) {
      // ポリゴン内の連続区間ごとに分割 (外に出た区間を結ばない)
      const poly = turf.polygon(polygonCoords)
      const out = []
      for (const path of paths) {
        let seg = []
        for (const p of path) {
          if (turf.booleanPointInPolygon(turf.point([p.lng, p.lat]), poly)) {
            seg.push(p)
          } else if (seg.length > 1) {
            out.push(seg)
            seg = []
          } else {
            seg = []
          }
        }
        if (seg.length > 1) out.push(seg)
      }
      return out
    },
    // 作業履歴 (派生) の評価: sources の Trajectory を取得し、range/clip を演算して描画 (Work は座標を持たない)
    async loadWorks() {
      this.worksLoaded = true
      this.workSegments = []
      try {
        const res = await feedApiClient.workRecords()
        this.works = res.data.records || []
        for (const w of this.works) {
          const paths = []
          for (const src of w.sources || []) {
            try {
              const r = await feedApiClient.getRoute(src.session_id, src.task_group_id)
              let pts = (r.data.route || []).map(p => ({ lat: p[0], lng: p[1] }))
              // 区間指定 (割合) があれば点列をスライスして評価
              if (src.range && pts.length > 1) {
                const from = src.range.from_pct != null ? src.range.from_pct : 0
                const to = src.range.to_pct != null ? src.range.to_pct : 100
                pts = pts.slice(Math.floor((pts.length * from) / 100), Math.ceil((pts.length * to) / 100))
              }
              if (pts.length > 1) paths.push(pts)
            } catch (e) {
              // 経路なしはスキップ
            }
          }
          let finalPaths = paths
          if (w.kind === 'clip' && w.clip_polygon && w.clip_polygon.coordinates) {
            finalPaths = this.clipPaths(paths, w.clip_polygon.coordinates)
          }
          for (const path of finalPaths) {
            this.workSegments.push({ id: w.id, path })
          }
        }
        // 作業履歴一覧「マップで表示」からの遷移 (?work=) は該当記録にズーム
        const focusWork = this.$route.query.work
        if (focusWork) {
          const segs = this.workSegments.filter(sg => sg.id === focusWork).map(sg => sg.path)
          if (segs.length) {
            this.fitToPaths(segs)
            this.workOpen = focusWork
          }
        }
      } catch (e) {
        console.error(e.response || e)
      }
    },
  },
}
</script>

<style scoped>
.map-page {
  position: relative;
}
.map-canvas {
  width: 100%;
  height: calc(100vh - 64px);
}
.map-poi-card {
  position: absolute;
  bottom: 24px;
  left: 12px;
  width: 260px;
  max-height: 50vh;
  overflow-y: auto;
  opacity: 0.96;
}
.map-layers {
  position: absolute;
  top: 12px;
  left: 12px;
  min-width: 180px;
  opacity: 0.94;
}
</style>
